import { BalloonEventModel, CategoryModel, MetadataModel } from '../../models';
import Transformer from '../../core/Transformer';
import { BalloonEventModelBuilder } from '../../models/BalloonEventModel';
import moment from 'moment';
import { EventDateModel } from '../../models/EventDateModel';

export default class BeatAPIBalloonEventTransformer extends Transformer<
  any,
  BalloonEventModel
> {
  constructor(
    private _builder: BalloonEventModelBuilder,
    private _metadata: MetadataModel,
    private _logs = false,
    plugins: Transformer<BalloonEventModel, BalloonEventModel>[] = []
  ) {
    super(plugins);
  }

  private _getSorting(closestDate: EventDateModel) {
    const msFromNow = closestDate.start.getTime() - Date.now();
    return 1 / msFromNow;
  }

  parseLocation(location: any) {
    return {
      lat: Number.parseFloat(
        Number.parseFloat(location.coordinates.lat).toFixed(10)
      ),
      lng: Number.parseFloat(
        Number.parseFloat(location.coordinates.lng).toFixed(10)
      ),
    };
  }

  protected _transform(data: any): BalloonEventModel {
    const locationData = Array.isArray(data.location)
      ? data.location[0]
      : data.location;
    
    // Handle null location from API - provide default empty location
    const location = locationData || {
      name: '',
      address: { street: '', postalCode: '', city: '', country: '' },
      coordinates: { lat: 0, lng: 0 },
      contactInfos: []
    };
    const dates: EventDateModel[] = data.dates
      .map((d: any) => {
        return new EventDateModel(
          moment(d.startDate).toDate(),
          d.endDate != null ? moment(d.endDate).toDate() : undefined,
          d.notes || ''
        );
      })
      .sort((a: EventDateModel, b: EventDateModel) => {
        return a.start.getTime() - b.start.getTime();
      });
    const transformed = this._builder
      .id(data.id)
      .slug(data.slug)
      .title(data.name)
      .tags(data.tags)
      .categories(
        data.categories
          .map((c: any) => {
            return this._metadata.getCategoryBySlug(c.slug);
          })
          .filter((c: CategoryModel | null) => c != null) as CategoryModel[]
      )
      .description(data.descriptions)
      .fullLocation(location)
      .contacts(data.contactInfos)
      .lastModified(
        data.updatedAt ? moment(data.updatedAt).toDate() : new Date()
      )
      .dates(dates)
      .source(
        this._metadata.getProviderById(data.dataSourceProvider.id) ||
          data.dataSourceProvider
      )
      .images(
        data.images.map((i: any) => {
          // Explicitly handle copyright - ensure it's always a string (guard null/undefined before String())
          const copyright = i.copyright != null ? String(i.copyright).trim() : ""

          const imageData = {
            url: i.url || '',
            text: i.text || '',
            copyright,
            sourceUrl: i.source_url || undefined,
            license: i.license || undefined,
            creditText: i.credit_text || undefined,
          };

          if (this._logs) {
            console.log('[BeatAPIBalloonEventTransformer] Image:', {
              url: imageData.url,
              hasCopyright: Boolean(imageData.copyright),
              copyright: imageData.copyright
            });
          }

          return imageData;
        })
      )
      .sorting(this._getSorting(dates[0]))
      .organizer(
        data.organisers && data.organisers.length > 0
          ? data.organisers.map((c: any) => {
              return {
                salutation: c.salutation,
                name: c.name,
                designation: c.designation,
                contacts: c.contactInfos,
              };
            })[0]
          : null
      )
      .collectionId(
        data.balloonCollection && data.balloonCollection.length > 0
          ? data.balloonCollection.map((c: any) => c.id)[0]
          : null
      )
      .attributes(data.attributes || {})
      .logo(data.logo ?? null)
      .creator(data.creator ?? null)
      .build();

    if (this._logs) {
      console.log('transformed', data, transformed, this._metadata);
    }

    return transformed;
  }
}
